import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
} from '@mui/icons-material';
import MDEditor from '@uiw/react-md-editor';
import ImageUploadPopup from '../../components/ImageUploadPopup/ImageUploadPopup';
import CompanyInfoEditPopup from '../../components/CompanyInfoEditPopup/CompanyInfoEditPopup';
import ContactInfoEditPopup from '../../components/ContactInfoEditPopup/ContactInfoEditPopup';
import LegalInfoEditPopup from '../../components/LegalInfoEditPopup/LegalInfoEditPopup';
import CategoriesEditPopup from '../../components/CategoriesEditPopup/CategoriesEditPopup';
import ServicePolicyEditPopup from '../../components/ServicePolicyEditPopup/ServicePolicyEditPopup';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { useVendorProfile } from '../../contexts/VendorProfileContext';
import { vendorProfileService, VendorProfile } from '../../services/vendorProfileService';
import { categoryService, CategoryLabelMaps, formatCategoryLabels } from '../../services/categoryService';
import { toast } from 'react-toastify';
import { occasionsService } from '../../services/occasionsService';
import { vendorOccasionsService } from '../../services/vendorOccasionsService';
import OccasionsEditPopup from '../../components/OccasionsEditPopup/OccasionsEditPopup';

// S3 asset URLs
const burgerPlaceholderBig = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/burger-placeholder-big.png';
const editInvertedIcon = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/edit-inverted.png';

const CompanyProfile: React.FC = () => {
  usePageTitle('Company Profile');
  const { vendor: sessionVendor } = useVendorAuth();
  const { updateProfileImage } = useVendorProfile();
  const [vendorProfile, setVendorProfile] = useState<VendorProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mainCategoryName, setMainCategoryName] = useState<string>('');
  const [categoryLabels, setCategoryLabels] = useState<CategoryLabelMaps | null>(null);
  const [isImageUploadOpen, setIsImageUploadOpen] = useState(false);
  const [isCompanyInfoOpen, setIsCompanyInfoOpen] = useState(false);
  const [isContactInfoOpen, setIsContactInfoOpen] = useState(false);
  const [isLegalInfoOpen, setIsLegalInfoOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isServicePolicyOpen, setIsServicePolicyOpen] = useState(false);
  const [occasions, setOccasions] = useState<Array<{ occasionId: string; title: string }>>([]);
  const [selectedOccasions, setSelectedOccasions] = useState<string[]>([]);
  const [isOccasionsOpen, setIsOccasionsOpen] = useState(false);
  const handleOccasionsSaved = (ids: string[]) => {
    setSelectedOccasions(ids);
  };

  useEffect(() => {
    const fetchVendorProfile = async () => {
      if (!sessionVendor) {
        setError('No vendor session found');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await vendorProfileService.getVendorProfile(sessionVendor.vendorId);
        
        if (response.success && response.vendor) {
          setVendorProfile(response.vendor);
        } else {
          setError(response.error || 'Failed to fetch vendor profile');
        }
      } catch (err) {
        console.error('Error fetching vendor profile:', err);
        setError('Failed to fetch vendor profile');
        toast.error('Failed to load company profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchVendorProfile();
  }, [sessionVendor]);

  // Load occasions catalogue
  useEffect(() => {
    const loadOccasions = async () => {
      try {
        const res = await occasionsService.getAllOccasions();
        const list = (res.data.occasions || []).map(o => ({ occasionId: o.occasionId, title: o.title }));
        setOccasions(list);
      } catch (e) {
        console.error('Error fetching occasions:', e);
      }
    };
    loadOccasions();
  }, []);

  // Load vendor selected occasions for display
  useEffect(() => {
    const loadSelected = async () => {
      try {
        if (!sessionVendor?.vendorId) return;
        const res = await vendorOccasionsService.getVendorOccasions(sessionVendor.vendorId);
        setSelectedOccasions((res.data.occasions || []).map(o => o.occasionId));
      } catch (e) {
        // silent
      }
    };
    loadSelected();
  }, [sessionVendor]);

  // Load catalog label maps once (display names for stored keys/IDs)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const maps = await categoryService.getCategoryLabelMaps();
        if (!cancelled) setCategoryLabels(maps);
      } catch (e) {
        console.error('Error loading category label maps:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve main category name when profile or label maps change
  useEffect(() => {
    const main = vendorProfile?.categoryTree?.mainCategory;
    if (!main) {
      setMainCategoryName('');
      return;
    }
    if (categoryLabels?.mainById[main]) {
      setMainCategoryName(categoryLabels.mainById[main]);
      return;
    }
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(main);
    if (isUUID) {
      fetchCategoryName(main);
    } else {
      setMainCategoryName(main);
    }
  }, [vendorProfile?.categoryTree?.mainCategory, categoryLabels]);

  // Function to fetch category name from API
  const fetchCategoryName = async (categoryId: string) => {
    try {
      const response = await categoryService.getAllCategories();
      if (response.message === 'Success' && response.data.categories) {
        let category = response.data.categories.find(
          cat => cat.categoryId === categoryId && cat.isActive
        );
        if (!category) {
          category = response.data.categories.find(
            cat => cat.categoryId === categoryId
          );
        }
        if (category) {
          setMainCategoryName(category.name);
        } else {
          setMainCategoryName('Unknown Category');
        }
      }
    } catch (error) {
      console.error('Error fetching category name:', error);
      setMainCategoryName('Unknown Category');
    }
  };

  // Helper function to get vendor's primary category name
  const getPrimaryCategory = (): string => {
    if (mainCategoryName) {
      return mainCategoryName;
    }
    if (vendorProfile?.categoryTree?.mainCategory) {
      const main = vendorProfile.categoryTree.mainCategory;
      if (categoryLabels?.mainById[main]) {
        return categoryLabels.mainById[main];
      }
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(main);
      if (isUUID) {
        return 'Loading...';
      }
      return main;
    }
    if (vendorProfile?.businessType) {
      return vendorProfile.businessType;
    }
    return '—';
  };

  // Subcategories are stored as catalog keys — resolve to names for display
  const getVendorTags = (): string => {
    if (!vendorProfile) return '—';
    if (vendorProfile.categoryTree?.subCategories?.length) {
      return (
        formatCategoryLabels(
          vendorProfile.categoryTree.subCategories,
          categoryLabels?.subByKey || {},
        ) || vendorProfile.categoryTree.subCategories.join(', ')
      );
    }
    return vendorProfile.businessType || '—';
  };

  // Types are stored as catalog keys — resolve to names for display
  const getVendorTypes = (): string => {
    if (vendorProfile?.categoryTree?.types?.length) {
      return (
        formatCategoryLabels(
          vendorProfile.categoryTree.types,
          categoryLabels?.typeByKey || {},
        ) || vendorProfile.categoryTree.types.join(', ')
      );
    }
    return '—';
  };

  // Items are already display strings in the catalog
  const getVendorItems = (): string => {
    if (vendorProfile?.categoryTree?.items?.length) {
      return vendorProfile.categoryTree.items.join(', ');
    }
    return '—';
  };

const getLocationsServed = (): string[] => {
  const profileWithLocations = vendorProfile as VendorProfile & { locationsServed?: string[] };

  if (profileWithLocations?.locationsServed && profileWithLocations.locationsServed.length > 0) {
    return profileWithLocations.locationsServed;
  }

  if (vendorProfile?.registeredAddress) {
    return [vendorProfile.registeredAddress];
  }

  return [];
};

const locationsServed = getLocationsServed();

  const formatShippingFeeDisplay = (amount: number | undefined): string => {
    if (amount === undefined || Number.isNaN(amount)) {
      return 'Not set';
    }
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
    return `${formatted} QAR`;
  };

  // Occasions selection handled in popup

  // Handler for opening image upload popup
  const handleImageEditClick = () => {
    setIsImageUploadOpen(true);
  };

  // Handler for closing image upload popup
  const handleImageUploadClose = () => {
    setIsImageUploadOpen(false);
  };

  // Handler for successful image update
  const handleImageUpdate = (newImageUrl: string) => {
    if (vendorProfile) {
      // Add cache-busting timestamp to force browser to reload the image
      const cacheBustedUrl = `${newImageUrl}?t=${Date.now()}`;
      setVendorProfile({
        ...vendorProfile,
        profileImageUrl: cacheBustedUrl
      });
      // Update the context so sidebar gets the new image
      updateProfileImage(newImageUrl);
    }
  };

  // Handler for company info update
  const handleCompanyInfoUpdate = (updatedData: Partial<VendorProfile>) => {
    if (vendorProfile) {
      setVendorProfile({
        ...vendorProfile,
        ...updatedData
      });
    }
  };

  // Handler for contact info update
  const handleContactInfoUpdate = (updatedData: Partial<VendorProfile>) => {
    if (vendorProfile) {
      setVendorProfile({
        ...vendorProfile,
        ...updatedData
      });
    }
  };

  // Handler for legal info update
  const handleLegalInfoUpdate = (updatedData: Partial<VendorProfile>) => {
    if (vendorProfile) {
      setVendorProfile({
        ...vendorProfile,
        ...updatedData
      });
    }
  };

  // Handler for categories update — keep keys/IDs in categoryTree; display resolves names
  const handleCategoriesUpdate = (updatedData: any) => {
    if (vendorProfile) {
      setVendorProfile({
        ...vendorProfile,
        ...updatedData
      });
      // mainCategoryName is refreshed by the useEffect when categoryTree.mainCategory changes
    }
  };


  // Handler for service policy update
  const handleServicePolicyUpdate = (updatedData: Partial<VendorProfile>) => {
    if (vendorProfile) {
      setVendorProfile({
        ...vendorProfile,
        ...updatedData
      });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#ffffff'
      }}>
        <CircularProgress sx={{ color: '#67314f' }} />
      </Box>
    );
  }

  if (error || !vendorProfile) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        width: '100%',
        backgroundColor: '#ffffff'
      }}>
        <Typography variant="h6" color="error">
          {error || 'Failed to load company profile'}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        component="main"
        sx={{
          width: '100%',
          bgcolor: '#ffffff',
          p: 0,
        }}
      >
        {/* Page Title */}
        <Box sx={{ 
          px: { xs: 2, sm: 4, md: 6, lg: 10 }, 
          py: { xs: 3, sm: 4, md: 5 } 
        }}>
          <Box sx={{ mb: 1 }}>
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '28px', sm: '32px', md: '38px', lg: '44px' },
                fontWeight: 700,
                color: '#181818',
                lineHeight: 1.15,
                letterSpacing: '-0.44px',
                mb: 1
              }}
            >
              Company profile
            </Typography>
          </Box>
          <Typography 
            sx={{ 
              fontSize: { xs: '14px', sm: '15px', md: '16px' },
              fontWeight: 400,
              color: '#181818',
              lineHeight: 1.4
            }}
          >
            Edit your profile to customise your company card appearance and settings
          </Typography>
        </Box>

        {/* Main Content */}
        <Box sx={{ 
          px: { xs: 2, sm: 4, md: 6, lg: 10 }, 
          pb: { xs: 3, sm: 4, md: 5 } 
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 3, sm: 4, lg: 5 },
            alignItems: 'flex-start'
          }}>
            {/* Left Column */}
            <Box sx={{ 
              flex: 1, 
              width: { xs: '100%', lg: 'auto' },
              maxWidth: { xs: '100%', lg: '500px' }
            }}>
              {/* Company Image */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ position: 'relative', mb: 2.5 }}>
                  <Box
                    sx={{
                      height: { xs: 140, sm: 160, md: 175 },
                      backgroundImage: `url(${vendorProfile.profileImageUrl || burgerPlaceholderBig})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: '12px',
                      border: '1px solid #c5cbd3'
                    }}
                  />
                  <IconButton
                    onClick={handleImageEditClick}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: '#ffffff',
                      width: 32,
                      height: 32,
                      borderRadius: '16px',
                      '&:hover': {
                        backgroundColor: '#f1f4f4',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={editInvertedIcon}
                      alt="Edit"
                      sx={{
                        width: 32,
                        height: 32,
                        objectFit: 'contain'
                      }}
                    />
                  </IconButton>
                </Box>
              </Box>

              {/* Primary Info */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography 
                    variant="h2" 
                    sx={{ 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '22px', sm: '24px', md: '26px', lg: '28px' },
                      fontWeight: 700,
                      lineHeight: 1.15,
                      color: '#181818',
                      margin: 0,
                      letterSpacing: '-0.28px'
                    }}
                  >
                    {vendorProfile.companyName || 'Company Name'}
                  </Typography>
                  <IconButton 
                    onClick={() => setIsCompanyInfoOpen(true)}
                    sx={{ 
                      width: 32, 
                      height: 32,
                      backgroundColor: '#67314f',
                      borderRadius: '16px',
                      '&:hover': {
                        backgroundColor: '#5a2a42',
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                  </IconButton>
                </Box>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 2, 
                    color: '#181818', 
                    lineHeight: 1.4,
                    fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: '16px',
                    fontWeight: 400
                  }}
                >
                  {vendorProfile.companyDescription || 'No description available. Please add a company description to showcase your business.'}
                </Typography>

                {vendorProfile.companyWebsite && (
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        lineHeight: 1.4,
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Website: {vendorProfile.companyWebsite}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#181818',
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '14px', sm: '15px', md: '16px' },
                      fontWeight: 700,
                      mb: 1,
                    }}
                  >
                    Shipping fees
                  </Typography>
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#181818',
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400,
                        flexShrink: 0,
                      }}
                    >
                      Inside Doha:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#181818',
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400,
                        textAlign: 'right',
                      }}
                    >
                      {formatShippingFeeDisplay(vendorProfile.shippingFeeInsideDoha)}
                    </Typography>
                  </Box>
                  <Box sx={{ py: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#181818',
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400,
                        flexShrink: 0,
                      }}
                    >
                      Outside Doha:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#181818',
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400,
                        textAlign: 'right',
                      }}
                    >
                      {formatShippingFeeDisplay(vendorProfile.shippingFeeOutsideDoha)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Contact Information */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '16px', sm: '17px', md: '18px' },
                      fontWeight: 700,
                      color: '#181818',
                      lineHeight: 1.25,
                      letterSpacing: '-0.18px'
                    }}
                  >
                    Contact Information
                  </Typography>
                  <IconButton 
                    onClick={() => setIsContactInfoOpen(true)}
                    sx={{ 
                      width: 32, 
                      height: 32,
                      backgroundColor: '#67314f',
                      borderRadius: '16px',
                      '&:hover': {
                        backgroundColor: '#5a2a42',
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                  </IconButton>
                </Box>
                
                <Box>
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Phone:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {vendorProfile.phone && vendorProfile.countryCode 
                        ? `${vendorProfile.countryCode} ${vendorProfile.phone}`
                        : vendorProfile.phone || 'Not provided'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Email:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {vendorProfile.email || 'Not provided'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Contact Person:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {vendorProfile.contactName || 'Not provided'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Service Category and Items */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '16px', sm: '17px', md: '18px' },
                      fontWeight: 700,
                      color: '#181818',
                      lineHeight: 1.25,
                      letterSpacing: '-0.18px'
                    }}
                  >
                    Service Category and Items
                  </Typography>
                  <IconButton 
                    onClick={() => setIsCategoriesOpen(true)}
                    sx={{ 
                      width: 32, 
                      height: 32,
                      backgroundColor: '#67314f',
                      borderRadius: '16px',
                      '&:hover': {
                        backgroundColor: '#5a2a42',
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                  </IconButton>
                </Box>
                
                <Box>
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Main Category:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {getPrimaryCategory()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Sub Categories:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {getVendorTags()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Service Types:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {getVendorTypes()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Service Items:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {getVendorItems()}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Occasions (similar to onboarding step 6) */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '16px', sm: '17px', md: '18px' },
                      fontWeight: 700,
                      color: '#181818',
                      lineHeight: 1.25,
                      letterSpacing: '-0.18px'
                    }}
                  >
                    Occasions
                  </Typography>
                  <IconButton 
                    onClick={() => setIsOccasionsOpen(true)}
                    sx={{ 
                      width: 32, 
                      height: 32,
                      backgroundColor: '#67314f',
                      borderRadius: '16px',
                      '&:hover': {
                        backgroundColor: '#5a2a42',
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                  </IconButton>
                </Box>
                <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#181818', 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: '16px',
                      fontWeight: 400,
                      flexShrink: 0
                    }}
                  >
                    Selected Occasions:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#181818', 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: '16px',
                      fontWeight: 400,
                      textAlign: 'right',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    {selectedOccasions.length > 0 ? occasions.filter(o => selectedOccasions.includes(o.occasionId)).map(o => o.title).join(', ') : 'None'}
                  </Typography>
                </Box>
              </Box>

              {/* Locations Served */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '16px', sm: '17px', md: '18px' },
                      fontWeight: 700,
                      color: '#181818',
                      lineHeight: 1.25,
                      letterSpacing: '-0.18px'
                    }}
                  >
                    Locations served
                  </Typography>
                </Box>
                
                <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#181818',
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: '16px',
                      fontWeight: 400
                    }}
                  >
                    {locationsServed?.join(', ') || 'Doha, Qatar'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Right Column */}
            <Box sx={{ 
              flex: 1, 
              width: { xs: '100%', lg: 'auto' },
              maxWidth: { xs: '100%', lg: '500px' }
            }}>
              {/* Legal Information */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '16px', sm: '17px', md: '18px' },
                      fontWeight: 700,
                      color: '#181818',
                      lineHeight: 1.25,
                      letterSpacing: '-0.18px'
                    }}
                  >
                    Legal Information
                  </Typography>
                  <IconButton 
                    onClick={() => setIsLegalInfoOpen(true)}
                    sx={{ 
                      width: 32, 
                      height: 32,
                      backgroundColor: '#67314f',
                      borderRadius: '16px',
                      '&:hover': {
                        backgroundColor: '#5a2a42',
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                  </IconButton>
                </Box>
                
                <Box>
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: { xs: '14px', sm: '15px', md: '16px' },
                        fontWeight: 400,
                        flexShrink: 0
                      }}
                    >
                      Trade License Number:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: { xs: '14px', sm: '15px', md: '16px' },
                        fontWeight: 400,
                        textAlign: 'right',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {vendorProfile.tradeLicenseNumber || 'Not provided'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: { xs: '14px', sm: '15px', md: '16px' },
                        fontWeight: 400,
                        flexShrink: 0
                      }}
                    >
                      Commercial Registration No.:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: { xs: '14px', sm: '15px', md: '16px' },
                        fontWeight: 400,
                        textAlign: 'right',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {vendorProfile.commercialRegistrationNo || 'Not provided'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Registered Name:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {vendorProfile.registeredName || 'Not provided'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Registered Address:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {vendorProfile.registeredAddress || 'Not provided'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Service Policy */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '16px', sm: '17px', md: '18px' },
                      fontWeight: 700,
                      color: '#181818',
                      lineHeight: 1.25,
                      letterSpacing: '-0.18px'
                    }}
                  >
                    Service Policy
                  </Typography>
                  <IconButton 
                    onClick={() => setIsServicePolicyOpen(true)}
                    sx={{ 
                      width: 32, 
                      height: 32,
                      backgroundColor: '#67314f',
                      borderRadius: '16px',
                      '&:hover': {
                        backgroundColor: '#5a2a42',
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16, color: '#ffffff' }} />
                  </IconButton>
                </Box>
                
                <Box>
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Accept orders not later than:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {vendorProfile.acceptOrdersDeadline !== undefined ? `${vendorProfile.acceptOrdersDeadline} hours` : 'Not specified'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      Accept changes not later than:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400
                      }}
                    >
                      {vendorProfile.acceptChangesDeadline !== undefined ? `${vendorProfile.acceptChangesDeadline} hours` : 'Not specified'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ py: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: { xs: '14px', sm: '15px', md: '16px' },
                        fontWeight: 400,
                        mb: 1
                      }}
                    >
                      Policy:
                    </Typography>
                    {vendorProfile.servicePolicy ? (
                      <Box
                        data-color-mode="light"
                        sx={{
                          '& .w-md-editor': {
                            backgroundColor: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                            '& .w-md-editor-bar': {
                              display: 'none',
                            },
                          },
                          '& .w-md-editor-text': {
                            display: 'none',
                          },
                          '& .w-md-editor-preview': {
                            backgroundColor: 'transparent',
                            padding: 0,
                            border: 'none',
                            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                            fontSize: '16px',
                            color: '#181818',
                            '& p': {
                              margin: 0,
                              marginBottom: '8px',
                              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                              fontSize: '16px',
                              color: '#181818',
                              '&:last-child': {
                                marginBottom: 0,
                              },
                            },
                            '& ul, & ol': {
                              margin: 0,
                              marginBottom: '8px',
                              paddingLeft: '20px',
                              '&:last-child': {
                                marginBottom: 0,
                              },
                            },
                            '& li': {
                              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                              fontSize: '16px',
                              color: '#181818',
                              marginBottom: '4px',
                            },
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                              color: '#181818',
                              marginTop: '16px',
                              marginBottom: '8px',
                              '&:first-of-type': {
                                marginTop: 0,
                              },
                            },
                            '& code': {
                              backgroundColor: '#f6f8f8',
                              padding: '2px 4px',
                              borderRadius: '4px',
                              fontSize: '14px',
                            },
                            '& pre': {
                              backgroundColor: '#f6f8f8',
                              padding: '12px',
                              borderRadius: '8px',
                              overflow: 'auto',
                            },
                            '& blockquote': {
                              borderLeft: '3px solid #67314f',
                              paddingLeft: '12px',
                              marginLeft: 0,
                              color: '#181818',
                            },
                            '& a': {
                              color: '#67314f',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            },
                          }
                        }}
                      >
                        <MDEditor
                          value={String(vendorProfile.servicePolicy || '')}
                          preview="preview"
                          hideToolbar={true}
                          visibleDragbar={false}
                        />
                      </Box>
                    ) : (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#181818', 
                          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                          fontSize: { xs: '14px', sm: '15px', md: '16px' },
                        fontWeight: 400,
                        }}
                      >
                        No service policy provided. Please add your service policy terms and conditions.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Image Upload Popup */}
      <ImageUploadPopup
        open={isImageUploadOpen}
        onClose={handleImageUploadClose}
        onUpdate={handleImageUpdate}
        vendorId={sessionVendor?.vendorId || ''}
        currentImageUrl={vendorProfile?.profileImageUrl}
      />

      {/* Company Info Edit Popup */}
      <CompanyInfoEditPopup
        open={isCompanyInfoOpen}
        onClose={() => setIsCompanyInfoOpen(false)}
        onUpdate={handleCompanyInfoUpdate}
        vendorProfile={vendorProfile}
      />

      {/* Contact Info Edit Popup */}
      <ContactInfoEditPopup
        open={isContactInfoOpen}
        onClose={() => setIsContactInfoOpen(false)}
        onUpdate={handleContactInfoUpdate}
        vendorProfile={vendorProfile}
      />

      {/* Legal Info Edit Popup */}
      <LegalInfoEditPopup
        open={isLegalInfoOpen}
        onClose={() => setIsLegalInfoOpen(false)}
        onUpdate={handleLegalInfoUpdate}
        vendorProfile={vendorProfile}
      />

      {/* Categories Edit Popup */}
      <CategoriesEditPopup
        open={isCategoriesOpen}
        onClose={() => setIsCategoriesOpen(false)}
        onUpdate={handleCategoriesUpdate}
        vendorProfile={vendorProfile}
      />


      {/* Service Policy Edit Popup */}
      <ServicePolicyEditPopup
        open={isServicePolicyOpen}
        onClose={() => setIsServicePolicyOpen(false)}
        onUpdate={handleServicePolicyUpdate}
        vendorProfile={vendorProfile}
      />

      {/* Occasions Edit Popup */}
      <OccasionsEditPopup
        open={isOccasionsOpen}
        onClose={() => setIsOccasionsOpen(false)}
        vendorId={sessionVendor?.vendorId || ''}
        onSaved={handleOccasionsSaved}
      />
    </Box>
  );
};

export default CompanyProfile;
